import React, { useState } from "react";
import axios from "axios";
import { useBrand } from "@/context/BrandContext";
import { Send, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="eyebrow text-black/60 mb-2 block">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const { brand } = useBrand();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: "error", msg: "Name, email, and message are required." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, { ...form, brand: brand.slug });
      setStatus({ state: "success", msg: "Thanks — we'll be in touch." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      setStatus({ state: "error", msg });
    }
  };

  const inputCls =
    "w-full border border-black/20 bg-white px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors font-mono";

  return (
    <section
      id="contact"
      className="section-pad bg-white"
      data-testid="contact-section"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow text-[color:var(--primary)] mb-4">
            ◆ Connect With Us
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tighter mb-6">
            Drop Us <br />A Line.
          </h2>
          <p className="text-black/70 leading-relaxed mb-8">
            Questions on pricing, pickup service, or a bulk industrial account?
            Reach out — we'll get back quickly. You're contacting{" "}
            <strong>{brand.name}</strong> in {brand.city}, {brand.state}.
          </p>
          <div className="space-y-4 border-t border-black/15 pt-6 font-mono text-sm">
            <div>
              <div className="eyebrow text-black/50">Phone</div>
              <a href={`tel:${brand.phone.replace(/[^0-9]/g, "")}`} className="text-lg">
                {brand.phone}
              </a>
            </div>
            <div>
              <div className="eyebrow text-black/50">Email</div>
              <a href={`mailto:${brand.email}`}>{brand.email}</a>
            </div>
            <div>
              <div className="eyebrow text-black/50">Address</div>
              <div>{brand.address}</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="border border-black/15 p-8 md:p-10 bg-[color:var(--bg-alt)]"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name *" testId="field-name">
                <input
                  type="text"
                  value={form.name}
                  onChange={onChange("name")}
                  className={inputCls}
                  placeholder="Your full name"
                  data-testid="contact-input-name"
                />
              </Field>
              <Field label="Email *" testId="field-email">
                <input
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  className={inputCls}
                  placeholder="you@example.com"
                  data-testid="contact-input-email"
                />
              </Field>
              <Field label="Phone" testId="field-phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={onChange("phone")}
                  className={inputCls}
                  placeholder="(xxx) xxx-xxxx"
                  data-testid="contact-input-phone"
                />
              </Field>
              <Field label="Yard" testId="field-brand">
                <input
                  readOnly
                  value={`${brand.short_name} · ${brand.city}`}
                  className={`${inputCls} bg-white/50 text-black/60`}
                  data-testid="contact-input-brand"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Message *" testId="field-message">
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={onChange("message")}
                    className={`${inputCls} resize-none`}
                    placeholder="Tell us about your materials, pickup needs, or questions…"
                    data-testid="contact-input-message"
                  />
                </Field>
              </div>
            </div>

            {status.state === "error" && (
              <div
                className="mt-5 p-3 border border-red-600/40 bg-red-50 text-red-700 text-xs font-mono flex items-center gap-2"
                data-testid="contact-error"
              >
                <AlertCircle size={14} /> {status.msg}
              </div>
            )}
            {status.state === "success" && (
              <div
                className="mt-5 p-3 border border-green-700/40 bg-green-50 text-green-800 text-xs font-mono flex items-center gap-2"
                data-testid="contact-success"
              >
                <Check size={14} /> {status.msg}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between gap-4">
              <div className="text-[11px] text-black/40 font-mono uppercase tracking-widest">
                We reply within 1 business day.
              </div>
              <button
                type="submit"
                disabled={status.state === "loading"}
                className="hard-btn-primary disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {status.state === "loading" ? "Sending…" : "Send Message"}
                <Send size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
